import React, { useCallback, useEffect, useState } from 'react';

import {
  Box,
  SwipeableDrawer,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { Province } from '../../@types/KingdomResponse';
import { KanbanColumn, KdKanban, ProvinceLite } from '../../@types/State';
import { useIntelState } from '../../context/IntelState';
import { setEnemyPlan, useEnemyData } from '../../hooks/useEnemyData';
import RestUtilities from '../../services/RestUtilities';
import { getCombo } from '../Intel/helpers/colHelpers';
import { initialState } from '../WarPlanner/helpers';
import Kanban from '../WarPlanner/Kanban';
import TopActions from '../WarPlanner/TopActions';

const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

// https://codesandbox.io/s/-w5szl?file=/src/index.js:2304-2334

const WarPlanner = () => {
  const { intelState, intelDispatch: dispatch } = useIntelState();
  const [province, setProvince] = useState<ProvinceLite | undefined>(undefined);
  const [message, setMessage] = useState<string>('');
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const state: KdKanban = intelState.enemyPlan
    ? intelState.enemyPlan
    : initialState;
  const provinces = intelState.enemyData?.provinces.map((x: Province) => {
    return {
      id: x.id,
      name: x.name,
      race: getCombo(x),
      slot: `[${x.id}]`,
    } as ProvinceLite;
  });

  useEnemyData();
  const matchesSmallWidth = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  const loadData = useCallback(async () => {
    setEnemyPlan(intelState.server, intelState.enemyKd, dispatch);
    setMessage('reloaded');
  }, [dispatch, intelState.enemyKd, intelState.server]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessage('');
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  }, [message]);

  const toggleDrawer = () => {
    setShowNotes(!showNotes);
  };

  const handleProvinceChange = (event: any) => {
    const prov: ProvinceLite | undefined = provinces?.find(
      (x: ProvinceLite) => x.id === event.target.value
    );
    if (prov) {
      setProvince(prov);
    }
  };

  const setState = (newState: KdKanban) => {
    dispatch({ type: 'SET_ENEMY_PLAN', payload: newState });
  };

  const handleContentChange = (event: any) => {
    const newState = { ...state, content: event.target.value };
    setState(newState);
  };

  const handleTitleChange = (event: any) => {
    const newState = { ...state, title: event.target.value };
    setState(newState);
  };

  const handleLoadClick = async (event: any) => {
    await loadData();
  };

  const handleSaveClick = async (event: any) => {
    const response = await RestUtilities.post(
      `WarPlan/v1/Post?server=${intelState.server}&location=${intelState.enemyKd}`,
      { json: JSON.stringify(state) }
    );
    if (response.ok) {
      setMessage('Saved');
    } else {
      setMessage('Something went wrong');
    }
  };

  return (
    <Box
      className='OPs planner'
      sx={{ display: 'flex', height: '100%', marginTop: 1 }}
    >
      <Box py={2} px={1}>
        <TopActions
          provinces={provinces}
          province={province}
          handleProvinceChange={handleProvinceChange}
          handleLoadClick={handleLoadClick}
          state={state}
          setState={setState}
          message={message}
          handleSaveClick={handleSaveClick}
          showNotes={showNotes}
          setShowNotes={setShowNotes}
          isWarPlan={true}
        />
        <Box mt={2} sx={{ display: matchesSmallWidth ? 'block' : 'flex' }}>
          <Kanban state={state} setState={setState} isWarPlan />
        </Box>
        <Box mt={2}>
          <Typography variant='h4' component='div' sx={{ marginRight: 2 }}>
            How to use
          </Typography>
          <Typography variant='body1' component='div' sx={{ marginRight: 2 }}>
            <ul>
              <li>
                You must save your data before leaving this page. Currently this
                is editable by all KD provinces. We will lock it down soon using
                the USERS page.
              </li>
              <li>
                Click ADD PROVINCES to add begin, or if you need to add
                provinces that have been deleted. Add a group and arrange your
                provinces by dragging and dropping a province into a groups.
                Groups titles will appear on the grid column if the
                &lsquo;Groups&lsquo; column is visible in your Grid setup.
              </li>
              <li>
                Add Optional province notes, they will also appear on grid if
                the &lsquo;Notes&lsquo; column is visible in your intel grid
                setup.
              </li>
              <li>
                To add orders to enemy provinces, expand the province and Click
                the ADD ORDERS button to show the options panel on the right
                side.
              </li>
              <li>
                There are 2 types of orders, with 2 variants. Required OPs
                (sticky) and Mission Objectives.
                <ol>
                  <li>
                    <ul>
                      <li>
                        Sticky Duration style OPs. (e.g. MS, Dro, Stor, Bliz,
                        IR, etc). These will appear/disappear in the
                        &lsquo;Req&lsquo; column, depending on whether or not
                        the province currenty has the OP active. It makes it
                        easy to see at a glance which OPS are priority to
                        perform when looking at the intel grid.
                      </li>
                      <li>
                        Sticky Instant damage style OPs. (e.g. NS, FB, SWH,
                        etc). These are visible at all times until you manually
                        delete them form the plan.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <ul>
                      <li>
                        Resource Objectives. (e.g. Food, Acres, Gold, Runes,
                        Horses). Use the slider to enter the target value for
                        the resource. We are not setting the value of damage, we
                        are setting the target value to{' '}
                        <i>leave the province with.</i> i.e. Get peasants down
                        to 2k. When the objective has been completed, it will be
                        removed automatically.
                      </li>
                      <li>
                        OP Type Objectives. (e.g. AR, AW, SW, LS). Enter the
                        number of Ops you want to be completed i.e. AR x5. When
                        the objective has been completed, it will be removed
                        automatically and we also add the Required OP to the
                        &lsquo;Req&lsquo; column for greater visibility on the
                        intel grid.
                      </li>
                    </ul>
                  </li>
                </ol>
              </li>
              <li>
                To view &lsquo;Notes&lsquo;, &lsquo;Groups&lsquo;,
                &lsquo;Objectives&lsquo; and &lsquo;Requests&lsquo; columns you
                might need to unhide them in your intel grid set up. It&lsquo;s
                possible you hid them at another time.
              </li>
            </ul>
          </Typography>
        </Box>
        <SwipeableDrawer
          anchor='right'
          open={showNotes}
          onClose={toggleDrawer}
          onOpen={toggleDrawer}
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
        >
          <Box p={2}>
            <Box>
              <TextField
                placeholder='Plan Title'
                name='plan-title'
                label='Plan Title'
                value={state.title}
                onChange={handleTitleChange}
                size='small'
                sx={{ marginBottom: '20px', width: '100%' }}
              />
              <TextField
                name='plan-notes'
                placeholder='Notes'
                label='Notes'
                value={state.content}
                onChange={handleContentChange}
                fullWidth
                multiline
                sx={{ marginBottom: '20px', width: '100%' }}
              />
            </Box>
            {!state.columns.some(
              (x: KanbanColumn) => x.title !== 'Unassigned'
            ) && (
              <Typography variant='h6' component='div' gutterBottom>
                No provinces assigned to groups
              </Typography>
            )}
            {state.columns.map((c: KanbanColumn, i: number) => {
              if (c.title === 'Unassigned') {
                return null;
              }
              return (
                <div key={`item_${c.title}_${i}`}>
                  <Typography
                    key={`col_${i}`}
                    variant='h6'
                    component='div'
                    gutterBottom
                  >
                    <span style={{ color: 'orange' }}>{c.title}</span>
                  </Typography>
                  {c.items &&
                    c.items.map((item: any, ii: number) => {
                      const name = `[${item.province.id}] ${item.province.name}`;
                      const note = item.content ? ` ${item.content}` : '';
                      const ops = item.province.requiredOps
                        ? ` (${item.province.requiredOps.join(',')})`
                        : '';
                      return (
                        <Typography
                          key={`item_${ii}`}
                          variant='h6'
                          component='div'
                          gutterBottom
                        >
                          <span>{`${name}${note}`}</span>
                          {ops}
                        </Typography>
                      );
                    })}
                </div>
              );
            })}
          </Box>
        </SwipeableDrawer>
      </Box>
    </Box>
  );
};

export default WarPlanner;
